// <SyqiOverview/> — SyQi's AI module on the home screen. Fetches (or
// generates) today's overview from /api/ai/overview plus its pre-rendered
// day-track audio, then hands everything to the shared <AiModule/>: the
// script, main points and suggestions are three views in the module's E
// dropdown, sharing the one text space with the live chat thread.
//
// The old text / voice / video mode split is gone (Joel 2026-07-26) — one
// module everywhere, with video on/off living in the module's ⋮ menu.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing, StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles, radii } from '../v10/theme';
import { CloudBackdrop } from './primitives';
import { apiFetch } from '../lib/api';
import { useSyqiChat } from './useSyqiChat';
import { AiModule, type AiModuleView } from './AiModule';
import type { VoiceTrack } from './useAiVoice';

interface OverviewRow {
  script: string;
  bullets: string[];
  suggestions: string[];
  conversation_id: string;
  date: string;
}

function Skeleton() {
  const styles = useStyles();
  const pulse = useRef(new Animated.Value(0.4)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 700, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0.4, duration: 700, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  return (
    <View style={styles.skeletonWrap}>
      <Animated.View style={[styles.skeletonLine, { width: '40%', opacity: pulse }]} />
      <Animated.View style={[styles.skeletonLine, { width: '92%', opacity: pulse }]} />
      <Animated.View style={[styles.skeletonLine, { width: '78%', opacity: pulse }]} />
    </View>
  );
}

export function SyqiOverview({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const [overview, setOverview] = useState<OverviewRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);

  const [track, setTrack] = useState<VoiceTrack | null>(null);
  const [trackLoading, setTrackLoading] = useState(true);
  // Bumped on every successful (re)load so the day-track refetches — a
  // regenerate on the same day returns the same `date`, so that can't be the key.
  const [version, setVersion] = useState(0);

  const load = useCallback(async (regenerate: boolean) => {
    if (regenerate) setRefreshing(true);
    else setLoading(true);
    setError(false);
    try {
      const res = regenerate
        ? await apiFetch<{ overview: OverviewRow; generated: boolean }>('/api/ai/overview', {
            method: 'POST',
            body: JSON.stringify({ regenerate: true }),
          })
        : await apiFetch<{ overview: OverviewRow; generated: boolean }>('/api/ai/overview');
      setOverview(res.overview);
      setVersion((v) => v + 1);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    load(false);
  }, [userId, load]);

  // Today's whole overview, rendered server-side into ONE seekable audio file
  // (stored on the overview row — refreshes reload it, never re-render it).
  useEffect(() => {
    if (!userId || version === 0) return;
    let cancelled = false;
    setTrackLoading(true);
    (async () => {
      try {
        const res = await apiFetch<{ audio: string; mime: string; lipsync?: VoiceTrack['lipsync'] }>(
          '/api/ai/overview/audio'
        );
        if (!cancelled) setTrack({ audio: res.audio, mime: res.mime, lipsync: res.lipsync ?? [] });
      } catch {
        // No overview yet (or render failed) — the module falls back to live TTS.
        if (!cancelled) setTrack(null);
      } finally {
        if (!cancelled) setTrackLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, version]);

  const { messages, sending, send, loadingMessages } = useSyqiChat({
    conversationId: overview?.conversation_id,
    persistLastConversation: false,
    enabled: !!overview?.conversation_id,
  });

  // The conversation is seeded with the overview script as message 0 so
  // follow-ups have it as context — but that script is already the "Daily
  // overview" view, so hide it from the chat stream.
  const visibleMessages = messages.length > 0 && messages[0].role === 'assistant' ? messages.slice(1) : messages;

  const views: AiModuleView[] = [
    {
      key: 'overview',
      label: 'Daily overview',
      speakText: overview?.script,
      render: () => <Text style={styles.script}>{overview?.script ?? ''}</Text>,
    },
    {
      key: 'points',
      label: 'Main points',
      speakText: overview?.bullets.join(' '),
      render: () =>
        overview && overview.bullets.length > 0 ? (
          <>
            {overview.bullets.map((b, i) => (
              <View key={i} style={styles.bulletRow}>
                <Text style={styles.bulletDot}>•</Text>
                <Text style={styles.bulletText}>{b}</Text>
              </View>
            ))}
          </>
        ) : (
          <Text style={styles.emptyText}>No main points today.</Text>
        ),
    },
    {
      key: 'suggests',
      label: 'SyQi suggests',
      speakText: overview?.suggestions.join(' '),
      render: () =>
        overview && overview.suggestions.length > 0 ? (
          <>
            {overview.suggestions.map((s, i) => (
              <View key={i} style={styles.suggestionChip}>
                <Text style={styles.suggestionText}>{s}</Text>
              </View>
            ))}
          </>
        ) : (
          <Text style={styles.emptyText}>Nothing suggested today.</Text>
        ),
    },
    { key: 'chat', label: 'Chat' },
  ];

  return (
    <AiModule
      stateKey="home"
      title="SyQi"
      navyLabelBar
      views={views}
      messages={visibleMessages}
      sending={sending}
      loadingMessages={loadingMessages}
      onSend={send}
      placeholder="Ask a follow-up…"
      emptyChatText="Ask SyQi about your day."
      track={track}
      trackLoading={trackLoading || loading}
      backdrop={<CloudBackdrop style={{ borderRadius: radii.panel }} />}
      menuItems={[
        {
          label: refreshing ? 'Regenerating…' : "Regenerate today's overview",
          onPress: () => load(true),
          disabled: refreshing || loading,
        },
      ]}
      banner={
        loading ? (
          <Skeleton />
        ) : error ? (
          <Text style={styles.errorText}>Couldn't load today's overview. Regenerate from the ⋮ menu.</Text>
        ) : null
      }
    />
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
    script: { fontSize: 15, lineHeight: 22, color: t.ivory },
    errorText: { fontSize: 13, color: t.lapsed, fontStyle: 'italic' },
    emptyText: { fontSize: 13, color: t.ivoryFaint, fontStyle: 'italic' },

    skeletonWrap: { gap: 8, paddingVertical: 4 },
    skeletonLine: { height: 12, borderRadius: 6, backgroundColor: t.track },

    bulletRow: { flexDirection: 'row', gap: 6 },
    bulletDot: { fontSize: 14, color: t.gold, lineHeight: 20 },
    bulletText: { flex: 1, fontSize: 14, lineHeight: 20, color: t.ivory },

    suggestionChip: {
      borderRadius: radii.tile,
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: c.lime,
      paddingHorizontal: 12,
      paddingVertical: 9,
    },
    suggestionText: { fontSize: 13, lineHeight: 18, color: t.goldBright, fontWeight: '600' },
  }),
);

export default SyqiOverview;
